import React, { useState } from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import dropDownData from './dropDownData';

const AI_ETHICS_MEMO = `CONFIDENTIAL - INTERNAL MEMO
================================
TO:   The AI Ethics Committee
      (which is just me at 2am)
FROM: Mahip Kakan, Principal Overthinker
DATE: ${new Date().toLocaleDateString()}
RE:   Our Company AI Ethics Policy
      (Draft 1. Not counting the 3 I deleted.)

EXECUTIVE SUMMARY
-----------------
We need an AI ethics policy. I have written one.
It is perfect. Do not question it. I have not slept.

SECTION 1: DO WE HAVE ETHICS?
-------------------------------
Yes. We have ethics. I checked. They're in a drawer somewhere.
Also, we have AI. Sometimes it hallucinates.
Just like I'm probably doing right now. Unclear.

SECTION 2: RESPONSIBLE USE OF AI
----------------------------------
AI shall be used responsibly. This means:

  - Not asking it to write your performance reviews
    (I mean... maybe once. I'm not your mom.)

  - Not trusting it when it says "As of my knowledge cutoff..."
    (that's just AI for "I'm about to be confidently wrong")

  - Not using AI-generated output in client deliverables without
    a human reviewing it first. We call this:
    "vibe checking the hallucination"

  - Not calling ChatGPT a "proprietary AI platform" in proposals.
    You know who you are.

SECTION 3: DATA PRIVACY
------------------------
Do NOT feed the AI our client data. I cannot stress this enough.
If you are reading this wondering "wait, did I already do that?" —
please schedule time with me immediately.
We will talk. It will be fine. Probably.

SECTION 4: BIAS AND FAIRNESS
------------------------------
AI models are biased. We are biased. This is a shared struggle.
We must audit for bias regularly.
I will put it in the quarterly roadmap.
It will get moved to next quarter.
This is the way.

SECTION 5: TRANSPARENCY
------------------------
When we use AI in a product, we must disclose this to users.
Unless the disclosure itself is generated by AI.
In which case... honestly I'll loop in Legal on this one.
(Legal does not know I'm doing this at 2am. Please don't tell them.)

SECTION 6: ACCOUNTABILITY
---------------------------
Who is responsible for AI decisions?
  - If it goes well:    The team
  - If it goes badly:   The model (allegedly)
  - If it goes REALLY badly: Mahip gets a calendar invite at 7am

SECTION 7: THE EXISTENTIAL STUFF
----------------------------------
Will AI replace us?

Short answer: some of us.

Long answer: The ones who learn to work WITH AI will replace the
ones who don't. So please, just... learn the thing.
I'll run a workshop. I'll bring snacks.
People come to things with snacks.

SECTION 8: DEFINITIONS
------------------------
"AI"           - A system that makes stuff up convincingly
"Hallucination" - When AI makes stuff up UNconvincingly
"Alignment"    - AI does what we want (we are still working on this)
"RAG"          - How we make AI make stuff up using OUR documents
"Agent"        - AI that makes stuff up AND takes actions about it
"PM"           - Person who explains all of the above to executives

CONCLUSION
-----------
This policy is effective immediately.
Or next sprint. Depending on capacity.

APPROVED BY:    Mahip Kakan
LEGAL REVIEW:   Pending (it's 2am, what do you want from me)
NEXT REVIEW:    Whenever I feel brave enough

P.S. If you're using GPT-4 for client work and calling it
     "proprietary AI", we need to have a very different meeting.

P.P.S. Go to sleep. Both of us.

[END OF DOCUMENT]
`;

export default function Notepad({ onClose }) {
  const [docText, setDocText] = useState(AI_ETHICS_MEMO);
  const [wordWrap, setWordWrap] = useState(true);

  function onClickOptionItem(item) {
    switch (item) {
      case 'Exit':
        onClose();
        break;
      case 'Word Wrap':
        setWordWrap(!wordWrap);
        break;
      case 'Time/Date':
        const date = new Date();
        setDocText(
          `${docText}${date.toLocaleTimeString()} ${date.toLocaleDateString()}`,
        );
        break;
      default:
    }
  }
  function onTextAreaKeyDown(e) {
    // handle tabs in text area
    if (e.which === 9) {
      e.preventDefault();
      e.persist();
      var start = e.target.selectionStart;
      var end = e.target.selectionEnd;
      setDocText(`${docText.substring(0, start)}\t${docText.substring(end)}`);

      // asynchronously update textarea selection to include tab
      // workaround due to https://github.com/facebook/react/issues/14174
      requestAnimationFrame(() => {
        e.target.selectionStart = start + 1;
        e.target.selectionEnd = start + 1;
      });
    }
  }

  return (
    <Div>
      <section className="np__toolbar">
        <WindowDropDowns items={dropDownData} onClickItem={onClickOptionItem} />
      </section>
      <StyledTextarea
        wordWrap={wordWrap}
        value={docText}
        onChange={e => setDocText(e.target.value)}
        onKeyDown={onTextAreaKeyDown}
        spellCheck={false}
      />
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  .np__toolbar {
    position: relative;
    height: 21px;
    flex-shrink: 0;
    border-bottom: 1px solid white;
  }
`;

const StyledTextarea = styled.textarea`
  flex: auto;
  outline: none;
  font-family: 'Lucida Console', monospace;
  font-size: 13px;
  line-height: 14px;
  resize: none;
  padding: 2px;
  ${props => (props.wordWrap ? '' : 'white-space: nowrap; overflow-x: scroll;')}
  overflow-y: scroll;
  border: 1px solid #96abff;
`;
